import { useQuery } from "@apollo/client/react";
import { useEffect, useMemo, useRef, type ReactNode } from "react";
import styled, { keyframes } from "styled-components";
import { themeGet } from "@styled-system/theme-get";
import {
  Badge,
  Box,
  Divider,
  Flex,
  H3,
  H5,
  Icon,
  Notification,
  P,
  Small,
  StyledLink,
} from "orcs-design-system";

import { FieldLabel, FieldStack } from "src/components/PositionFieldLayout";
import ApprovalRoutingChips from "src/components/ApprovalRoutingChips";
import GuardrailCheckList, {
  GuardrailCardHeader,
} from "src/components/GuardrailCheckList";
import { ExceptionReasonAttribution } from "src/components/exceptionReasonAttribution";
import ExceptionReasonForm from "src/components/ExceptionReasonForm";
import GuardrailDecisionForm from "src/components/GuardrailDecisionForm";
import { getGuardrailDecisionDisplay } from "src/components/RequisitionGuardrailOutcome";
import {
  useGuardrailChainActions,
  type ChainWriteOutcome,
} from "src/components/useGuardrailChainActions";
import { useGuardrailRuleLabels } from "src/components/guardrailRuleLabels";
import useWorkspaceDateFormats from "src/hooks/useWorkspaceDateFormats";
import { formatRelativeAgo } from "src/pages/WorkforceGuardrails/clockHours";
import { positionDetailsPath } from "src/util/routing";

import InboxPaneLoading from "../InboxPaneLoading";
import GuardrailHistory from "./GuardrailHistory";
import { formatWorkspaceStamp, guardrailHistory } from "./guardrailHistoryRows";
import { inboxRequisitionLookup } from "./lookup.graphql";
import {
  approvalStatus,
  decisionTarget,
  failingCheckLabels,
  labelInboxRequisition,
  personLabel,
  pillOf,
  readOnlyRequisition,
  type InboxRequisition,
  type RequisitionInboxItem,
} from "./item";

const fadeUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(var(--inbox-enter-offset));
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const EnterPanel = styled(Flex)`
  --inbox-enter-offset: ${themeGet("space.r")};
  animation: ${fadeUp} 240ms cubic-bezier(0.2, 0, 0, 1);

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const DetailTitle = styled(H3)`
  &:focus {
    outline: none;
  }
`;

const Fact = ({ label, value }: { label: string; value: string }) => {
  if (value === "") {
    return null;
  }
  return (
    <FieldStack>
      <FieldLabel>{label}</FieldLabel>
      <P m="0">{value}</P>
    </FieldStack>
  );
};

const fteText = (fte: number | null | undefined): string => {
  if (typeof fte !== "number" || !Number.isFinite(fte)) {
    return "";
  }
  return `${fte} FTE`;
};

/** Kind switch only. Interstitial work should wrap this, not inline it. */
const DetailFooter = ({
  kind,
  readOnly,
  shownId,
  target,
  chain,
}: {
  kind: RequisitionInboxItem["kind"];
  readOnly: boolean;
  shownId: string;
  target: ReturnType<typeof decisionTarget>;
  chain: ReturnType<typeof useGuardrailChainActions>;
}) => {
  const headingId = `inbox-ask-${shownId}`;
  let heading: string;
  let echo: ReactNode = null;
  let body: ReactNode = null;
  switch (kind) {
    case "REQUISITION_APPROVAL":
      heading = "Your decision";
      if (target) {
        echo = (
          <Flex alignItems="center" gap="s" flexShrink={0}>
            <Small color="subText">
              Approving as {target.group.approverGroupName}
            </Small>
            {target.group.pastSla ? (
              <Badge variant="danger">Past SLA</Badge>
            ) : null}
          </Flex>
        );
        body = (
          <GuardrailDecisionForm
            key={`${shownId}-${target.group.approverGroupId}-${target.order}`}
            group={target.group}
            order={target.order}
            actions={chain.actions}
            layout="footer"
          />
        );
      }
      break;
    case "REQUISITION_EXCEPTION_REASON":
      heading = "Exception reason";
      echo = <Badge variant="warning">Waiting for your reason</Badge>;
      body =
        !readOnly && chain.pendingReasonStep ? (
          <ExceptionReasonForm
            layout="footer"
            draft={chain.reasonDraft}
            saving={chain.actions.saving}
            error={null}
            fieldId={`inbox-exception-reason-${shownId}`}
            onChange={chain.onReasonDraft}
            onSave={chain.actions.saveReason}
          />
        ) : null;
      break;
    default: {
      const unexpected: never = kind;
      return unexpected;
    }
  }
  if (!body) {
    return null;
  }
  return (
    <Box
      as="section"
      aria-labelledby={headingId}
      data-testid="inbox-detail-ask"
      flexShrink={0}
      p="r"
      bg="appBackground"
      borderTop="1px solid"
      borderColor="surfaceBorder"
    >
      <Flex justifyContent="space-between" alignItems="center" gap="s" mb="s">
        <H5 id={headingId} m="0">
          {heading}
        </H5>
        {echo}
      </Flex>
      {chain.actionError ? (
        <P fontSize="1" m="0" mb="s">
          {chain.actionError}
        </P>
      ) : null}
      {body}
    </Box>
  );
};

export const RequisitionInboxDetail = ({
  item,
  requisition,
  readOnly,
  onDecided,
  onRespondStart,
  onRespondError,
  autoFocusTitle = false,
}: {
  item: {
    title: string;
    kind: RequisitionInboxItem["kind"];
    approverGroupId?: string;
  };
  requisition: InboxRequisition;
  readOnly: boolean;
  onDecided?: (decision: {
    response: ChainWriteOutcome;
    requisitionId: string;
    displayId: string;
    title: string;
  }) => void;
  onRespondStart?: () => void;
  onRespondError?: () => void;
  autoFocusTitle?: boolean;
}) => {
  const labels = useGuardrailRuleLabels(true);
  const labeled = useMemo(
    () =>
      labels.ready
        ? labelInboxRequisition(requisition, labels.rules)
        : requisition,
    [labels.ready, labels.rules, requisition]
  );
  const formats = useWorkspaceDateFormats();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const shown = useMemo(
    () => (readOnly ? readOnlyRequisition(labeled) : labeled),
    [readOnly, labeled]
  );
  const chain = useGuardrailChainActions(shown, readOnly, undefined, {
    ...(onRespondStart ? { onRespondStart } : {}),
    ...(onRespondError ? { onRespondError } : {}),
    onWritten: (_written, response) => {
      onDecided?.({
        response,
        requisitionId: shown.id ?? "",
        displayId: shown.externalId?.trim() || shown.id?.trim() || "",
        title: item.title,
      });
    },
  });
  const evaluation =
    chain.shown?.guardrailEvaluation ?? shown.guardrailEvaluation ?? null;
  const checks = evaluation?.checks ?? [];
  const steps = evaluation?.steps ?? [];
  const failing = failingCheckLabels(checks);
  const status = approvalStatus(
    steps,
    evaluation?.decision,
    evaluation?.template
  );
  const hasApprovalGroups = steps.some(
    (step) => step.kind === "group" && (step.groups?.length ?? 0) > 0
  );
  const templateBadge = getGuardrailDecisionDisplay(
    evaluation?.decision,
    evaluation?.template
  );
  const pill = pillOf(item.kind);
  const target =
    item.kind === "REQUISITION_APPROVAL"
      ? decisionTarget(steps, item.approverGroupId ?? "", evaluation?.decision)
      : null;
  const created = formatWorkspaceStamp(shown.openedDate, "date", formats);
  const assessedAt = evaluation?.evaluatedAt;
  const assessed =
    typeof assessedAt === "string"
      ? formatRelativeAgo(assessedAt, chain.actions.nowMs)
      : null;
  const when = [
    created ? `Created ${created}` : null,
    assessed ? `Last assessed ${assessed}` : null,
  ]
    .filter((part): part is string => part != null)
    .join(" · ");
  const reasonStep = (evaluation?.steps ?? []).find(
    (step) => step.kind === "reason"
  );
  const reasonText = reasonStep?.reason?.trim() ?? "";
  const showReason = reasonStep != null && (reasonText !== "" || readOnly);
  const history = guardrailHistory({
    openedDate: shown.openedDate ?? null,
    guardrailEvaluation: evaluation,
    guardrailDecisionLog:
      chain.shown?.guardrailDecisionLog ?? shown.guardrailDecisionLog ?? null,
  });
  const displayId = shown.externalId?.trim() || shown.id?.trim() || "";
  const positionId = shown.positionId?.trim() ?? "";
  const positionTo = positionId
    ? positionDetailsPath({ id: positionId })
    : null;
  useEffect(() => {
    if (autoFocusTitle) {
      titleRef.current?.focus();
    }
  }, [autoFocusTitle]);
  return (
    <EnterPanel flexDirection="column" width="100%" height="100%" minHeight="0">
      <Box flex="1" overflow="auto" p="r">
        <Flex flexDirection="column" gap="r" width="100%">
          {readOnly ? (
            <Notification variant="warning" closable={false}>
              This no longer needs your action
            </Notification>
          ) : null}
          <Flex justifyContent="space-between" alignItems="flex-start" gap="s">
            <Box minWidth="0">
              <Flex gap="xs" alignItems="center" flexWrap="wrap">
                <Badge variant={pill.variant}>{pill.label}</Badge>
                {displayId ? (
                  positionTo ? (
                    <StyledLink to={positionTo} small>
                      {displayId}
                    </StyledLink>
                  ) : (
                    <Small color="greyDark">{displayId}</Small>
                  )
                ) : null}
              </Flex>
              <DetailTitle
                m="0"
                mt="xs"
                tabIndex={autoFocusTitle ? -1 : undefined}
                ref={titleRef}
              >
                {item.title}
              </DetailTitle>
              {when ? (
                <Small color="greyDark" display="block" mt="xs">
                  {when}
                </Small>
              ) : null}
            </Box>
            <Flex
              flexDirection="column"
              alignItems="flex-end"
              gap="xs"
              flexShrink={0}
            >
              <Badge variant={status.variant}>{status.label}</Badge>
              {failing.length > 0 ? (
                <Small color="warningDarker">
                  <Icon
                    icon="exclamation-triangle"
                    variant="warning"
                    mr="xs"
                    aria-hidden="true"
                  />
                  {failing.join(", ")}
                </Small>
              ) : null}
            </Flex>
          </Flex>
          <Box>
            <Flex gap="l" flexWrap="wrap">
              <Fact
                label="Supply team"
                value={shown.supplyTeam?.name?.trim() ?? ""}
              />
              <Fact label="FTE" value={fteText(shown.fte)} />
              <Fact label="Location" value={shown.location?.trim() ?? ""} />
              <Fact
                label="Start"
                value={
                  formatWorkspaceStamp(
                    shown.targetStartDate,
                    "date",
                    formats
                  ) ?? ""
                }
              />
              <Fact
                label="Hiring manager"
                value={personLabel(shown.hiringManager)}
              />
            </Flex>
            <Divider light mt="r" />
          </Box>
          {templateBadge ? (
            <Box boxBorder="default" borderRadius={2}>
              <GuardrailCardHeader
                badge={hasApprovalGroups ? templateBadge : null}
                p="s"
                bg="greyLightest"
              />
              <GuardrailCheckList checks={checks} />
            </Box>
          ) : null}
          {shown.justification?.trim() ? (
            <Flex flexDirection="column" gap="xs">
              <Small uppercase weight="bold" color="greyDark">
                Justification
              </Small>
              <P m="0">{shown.justification.trim()}</P>
            </Flex>
          ) : null}
          {showReason ? (
            <Flex flexDirection="column" gap="xs">
              <Small uppercase weight="bold" color="greyDark">
                Exception justification
              </Small>
              <P m="0">{reasonText || "Not provided"}</P>
              <ExceptionReasonAttribution
                personName={reasonStep?.personName}
                personId={reasonStep?.personId}
                stamp={
                  typeof reasonStep?.actedAt === "string"
                    ? formatRelativeAgo(reasonStep.actedAt, chain.actions.nowMs)
                    : null
                }
              />
            </Flex>
          ) : null}
          <ApprovalRoutingChips
            steps={steps}
            template={evaluation?.template}
            failingNames={failing}
            nowMs={chain.actions.nowMs}
          />
          <GuardrailHistory rows={history} />
        </Flex>
      </Box>
      <DetailFooter
        kind={item.kind}
        readOnly={readOnly}
        shownId={shown.id ?? ""}
        target={target}
        chain={chain}
      />
    </EnterPanel>
  );
};

type LookupQuery = {
  result?: { requisitions?: InboxRequisition[] | null } | null;
};

const RegisteredRequisitionDetail = ({
  item,
  readOnly,
  onDecided,
  onRespondStart,
  onRespondError,
  autoFocusTitle,
}: {
  item: RequisitionInboxItem;
  readOnly: boolean;
  onDecided?: (decision: {
    response: ChainWriteOutcome;
    requisitionId: string;
    displayId: string;
    title: string;
  }) => void;
  onRespondStart?: () => void;
  onRespondError?: () => void;
  autoFocusTitle?: boolean;
}) => {
  const lookup = useQuery<LookupQuery>(inboxRequisitionLookup, {
    variables: {
      searchTerms: [item.requisitionId],
      size: 20,
    },
  });
  const found =
    lookup.dataState === "complete"
      ? (lookup.data.result?.requisitions?.find(
          (row) => row.id === item.requisitionId
        ) ?? null)
      : null;
  if (lookup.loading) {
    return <InboxPaneLoading />;
  }
  if (lookup.error || lookup.dataState !== "complete" || !found) {
    return (
      <Box p="r">
        <Notification variant="error" closable={false}>
          This requisition could not be loaded.
        </Notification>
      </Box>
    );
  }
  return (
    <RequisitionInboxDetail
      item={item}
      requisition={found}
      readOnly={readOnly}
      {...(onDecided ? { onDecided } : {})}
      {...(onRespondStart ? { onRespondStart } : {})}
      {...(onRespondError ? { onRespondError } : {})}
      {...(autoFocusTitle ? { autoFocusTitle } : {})}
    />
  );
};

export default RegisteredRequisitionDetail;
